import { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { Heading, Text, Spinner, Card, Badge, Button } from 'frosted-ui';
import { Lock16, Checkmark16 } from '@frosted-ui/icons';
import { loadWhop } from '@whop/elements';
import {
  WhopElements,
  Checkout,
  CheckoutElement,
  type CheckoutPaymentCompletion,
  type CheckoutWaitlistCompletion,
  type CheckoutSetupCompletion,
} from '@whop/elements-react';
import Rise from '../components/animation/Rise';
import Reveal from '../components/animation/Reveal';
import { api } from '../services/api';
import { trackOfferPageView } from '../utils/whopPixel';
import { useDocumentTitle } from '../hooks/useDocumentTitle';
import DeadLinkNotice from '../components/DeadLinkNotice';
import type { PublicOffer, SellerInfo } from '../types';

const VISITOR_ID_KEY = 'pwl_vid';
// Keyed by offer id; a migrated post's tokens stay under its post id.
const unlockTokenKey = (offerId: string) => `pwl_unlock_${offerId}`;

function getVisitorId(): string {
  let vid = localStorage.getItem(VISITOR_ID_KEY);
  if (!vid) {
    vid = crypto.randomUUID();
    localStorage.setItem(VISITOR_ID_KEY, vid);
  }
  return vid;
}

const CLAIM_RETRIES = 3;
const CLAIM_RETRY_DELAY_MS = 1500;

type UnlockState =
  | { phase: 'locked' }
  | { phase: 'claiming' }
  | { phase: 'unlocked'; contentUrl: string }
  | { phase: 'claim_failed'; message: string };

/**
 * The hosted offer page: blurred preview + embedded Whop checkout, unlocking
 * in place. Serves unlock tests (`/g/…`, where BuyPage passes the resolved
 * offer — the router already assigned the variant and recorded the visit)
 * and legacy `/p/…` links (resolved here: a migrated post's unlock test, or
 * the post itself while unmigrated).
 */
export default function OfferPage({ initialOffer }: { initialOffer?: PublicOffer }) {
  const { companyRoute, shareCode } = useParams<{
    companyRoute: string;
    shareCode: string;
  }>();
  const [searchParams, setSearchParams] = useSearchParams();

  const [offer, setOffer] = useState<PublicOffer | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [deadLinkSeller, setDeadLinkSeller] = useState<SellerInfo | null>(null);
  const [unlock, setUnlock] = useState<UnlockState>({ phase: 'locked' });
  const [restoreError, setRestoreError] = useState<string | null>(null);
  const [restoring, setRestoring] = useState(false);
  useDocumentTitle(offer?.title);

  // A link that leads nowhere still names the seller, when the store is known.
  useEffect(() => {
    if (!loadError || !companyRoute) return;
    api
      .getSeller(companyRoute)
      .then(setDeadLinkSeller)
      .catch(() => setDeadLinkSeller(null));
  }, [loadError, companyRoute]);

  const returnPath = window.location.pathname;

  const unlockWithToken = useCallback(
    async (offer: PublicOffer, token: string, storeUnder = offer.offerId) => {
      const contentUrl = await api.getOfferContentUrl(offer, token);
      localStorage.setItem(unlockTokenKey(storeUnder), token);
      setUnlock({ phase: 'unlocked', contentUrl });
    },
    []
  );

  // Resolve the offer, then try any stored unlock token or a pending
  // OAuth restore (?restore=1 set by the login return path).
  useEffect(() => {
    if (!initialOffer && (!companyRoute || !shareCode)) return;
    let cancelled = false;

    (async () => {
      try {
        const resolved =
          initialOffer ?? (await api.resolveLegacyOffer(companyRoute!, shareCode!, getVisitorId()));
        if (cancelled) return;
        setOffer(resolved);
        trackOfferPageView(resolved.companyId);

        const storedKeys = [resolved.offerId, resolved.legacyPostId].filter(
          (key): key is string => Boolean(key)
        );
        for (const key of storedKeys) {
          const stored = localStorage.getItem(unlockTokenKey(key));
          if (!stored) continue;
          try {
            await unlockWithToken(resolved, stored, key);
            return;
          } catch {
            localStorage.removeItem(unlockTokenKey(key));
          }
        }

        if (searchParams.get('restore') === '1') {
          setSearchParams({}, { replace: true });
          try {
            setRestoring(true);
            const { token } = await api.restoreOfferPurchase(resolved);
            await unlockWithToken(resolved, token);
          } catch (err: unknown) {
            setRestoreError(
              (err instanceof Error && err.message) ||
                'No purchase found for that Whop account.'
            );
          } finally {
            setRestoring(false);
          }
        }
      } catch (err: unknown) {
        if (!cancelled) {
          setLoadError(
            (err instanceof Error && err.message) ||
              'This link is invalid or has been removed.'
          );
        }
      }
    })();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyRoute, shareCode, initialOffer]);

  const handleComplete = useCallback(
    async (receiptId?: string) => {
      if (!offer) return;
      if (!receiptId) {
        setUnlock({
          phase: 'claim_failed',
          message:
            'Payment received, but we could not confirm it automatically. Use "Restore purchase" below to unlock.',
        });
        return;
      }

      setUnlock({ phase: 'claiming' });

      // The claim endpoint verifies against Whop directly, but give the
      // payment a few tries in case it is still settling.
      for (let attempt = 0; attempt < CLAIM_RETRIES; attempt++) {
        try {
          const { token } = await api.claimOfferPurchase(offer, receiptId);
          await unlockWithToken(offer, token);
          return;
        } catch {
          await new Promise((r) => setTimeout(r, CLAIM_RETRY_DELAY_MS * (attempt + 1)));
        }
      }

      setUnlock({
        phase: 'claim_failed',
        message:
          'Payment received, but unlocking timed out. Refresh this page or use "Restore purchase" to unlock.',
      });
    },
    [offer, unlockWithToken]
  );

  const handleCheckoutComplete = useCallback(
    (
      completion:
        | CheckoutPaymentCompletion
        | CheckoutWaitlistCompletion
        | CheckoutSetupCompletion
    ) => {
      handleComplete(completion.result === 'payment' ? completion.paymentId : undefined);
    },
    [handleComplete]
  );

  // A paid checkout leaves the page and comes back: after an off-site step
  // (3DS, PayPal, bank redirects) to returnUrl with ?payment=pay_...&status=
  // succeeded, and after any other payment to the checkout's redirect URL with
  // ?payment_id=pay_...&status=success.
  useEffect(() => {
    if (!offer || unlock.phase !== 'locked') return;
    const paymentId = searchParams.get('payment') ?? searchParams.get('payment_id');
    const status = searchParams.get('status');
    if (!paymentId || !status) return;

    setSearchParams({}, { replace: true });
    if (status === 'succeeded' || status === 'success') {
      handleComplete(paymentId);
    } else if (status === 'failed') {
      setUnlock({
        phase: 'claim_failed',
        message: 'Your payment did not go through. You have not been charged — please try again.',
      });
    }
  }, [offer, unlock.phase, searchParams, setSearchParams, handleComplete]);

  // Loaded only when a checkout renders; repeat calls share one script and promise
  const whopElements = useMemo(
    () => (offer?.checkoutConfigId && offer.availability === 'open' ? loadWhop() : null),
    [offer?.checkoutConfigId, offer?.availability]
  );

  const restoreHref = `/api/auth/whop/login?return=${encodeURIComponent(
    `${returnPath}?restore=1`
  )}`;

  // ---------- render ----------

  if (loadError) return <DeadLinkNotice seller={deadLinkSeller} />;

  if (!offer) {
    return (
      <CenteredShell>
        <Spinner size="2" />
      </CenteredShell>
    );
  }

  const unlocked = unlock.phase === 'unlocked';
  const aspectRatio =
    offer.width && offer.height ? `${offer.width} / ${offer.height}` : '4 / 3';

  return (
    <div
      style={{
        minHeight: '100vh',
        backgroundColor: 'var(--color-background)',
        display: 'flex',
        justifyContent: 'center',
        padding: 'var(--space-4)',
      }}
    >
      <div style={{ width: '100%', maxWidth: 480 }}>
        <Rise>
          <Text
            size="2"
            color="gray"
            style={{ display: 'block', textAlign: 'center', margin: 'var(--space-4) 0 var(--space-2)' }}
          >
            {offer.companyName}
          </Text>
          <Heading size="6" style={{ textAlign: 'center', marginBottom: 'var(--space-1)' }}>
            {offer.title}
          </Heading>
          {offer.description && (
            <Text
              size="2"
              color="gray"
              style={{ display: 'block', textAlign: 'center', marginBottom: 'var(--space-2)' }}
            >
              {offer.description}
            </Text>
          )}
        </Rise>

        <Rise delay={80}>
          <div
            style={{
              position: 'relative',
              borderRadius: 'var(--radius-4)',
              overflow: 'hidden',
              margin: 'var(--space-4) 0',
              backgroundColor: 'var(--gray-a3)',
              aspectRatio,
            }}
          >
            {unlocked ? (
              <Reveal>
                <img
                  src={unlock.contentUrl}
                  alt={offer.title}
                  style={{ width: '100%', height: '100%', objectFit: 'contain', display: 'block' }}
                />
              </Reveal>
            ) : (
              <>
                {offer.previewUrl && (
                  <img
                    src={offer.previewUrl}
                    alt="Locked preview"
                    style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                  />
                )}
                <div
                  style={{
                    position: 'absolute',
                    inset: 0,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 'var(--space-2)',
                    background: 'rgba(0, 0, 0, 0.25)',
                  }}
                >
                  <div
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      width: 48,
                      height: 48,
                      borderRadius: '50%',
                      backgroundColor: 'rgba(0, 0, 0, 0.55)',
                    }}
                  >
                    <Lock16 style={{ width: 22, height: 22, color: 'white' }} />
                  </div>
                  <Badge color="gray" highContrast>
                    Unlocks after purchase
                  </Badge>
                </div>
              </>
            )}
          </div>
        </Rise>

        {unlocked && (
          <Rise>
            <Card size="2" style={{ textAlign: 'center', marginBottom: 'var(--space-4)' }}>
              <Checkmark16 style={{ width: 18, height: 18, color: 'var(--green-11)', verticalAlign: 'middle', marginRight: 6 }} />
              <Text size="2" weight="medium">
                Unlocked — this page stays yours. Coming back on another device?
                Use{' '}
                <a href={restoreHref} style={{ color: 'var(--accent-11)' }}>
                  Restore purchase
                </a>
                .
              </Text>
            </Card>
          </Rise>
        )}

        {!unlocked && offer.availability === 'open' && offer.checkoutConfigId && (
          <Rise delay={160}>
            {unlock.phase === 'claiming' ? (
              <Card size="3" style={{ textAlign: 'center', padding: 'var(--space-6)' }}>
                <Spinner size="2" />
                <Text size="2" color="gray" style={{ display: 'block', marginTop: 'var(--space-3)' }}>
                  Confirming your purchase…
                </Text>
              </Card>
            ) : (
              <>
                {/* Whop's checkout below shows the price; don't repeat it. */}
                <Text
                  size="2"
                  color="gray"
                  style={{ display: 'block', textAlign: 'center', marginBottom: 'var(--space-3)' }}
                >
                  One-time purchase · unlocks instantly
                </Text>
                {unlock.phase === 'claim_failed' && (
                  <Card size="2" style={{ marginBottom: 'var(--space-3)', border: '1px solid var(--amber-a6)' }}>
                    <Text size="2" color="amber">
                      {unlock.message}
                    </Text>
                  </Card>
                )}
                <WhopElements
                  elements={whopElements}
                  appearance={{ theme: { appearance: 'dark', accentColor: 'iris', grayColor: 'slate' } }}
                  onLoadError={() =>
                    setUnlock({
                      phase: 'claim_failed',
                      message: 'Checkout failed to load. Refresh the page to try again.',
                    })
                  }
                >
                  <Checkout
                    checkoutConfiguration={offer.checkoutConfigId}
                    {...(offer.checkout?.metadata && { metadata: offer.checkout.metadata })}
                    {...(offer.checkout?.affiliateCode && { affiliateCode: offer.checkout.affiliateCode })}
                    {...(offer.checkout?.attribution && { attribution: offer.checkout.attribution })}
                    returnUrl={`${window.location.origin}${window.location.pathname}`}
                    onComplete={handleCheckoutComplete}
                  >
                    <CheckoutElement fallback={<Spinner size="2" />} />
                  </Checkout>
                </WhopElements>
              </>
            )}
          </Rise>
        )}

        {!unlocked && offer.availability !== 'open' && (
          <Rise delay={160}>
            <Card size="3" style={{ textAlign: 'center' }}>
              <Heading size="4" style={{ marginBottom: 'var(--space-2)' }}>
                {offer.availability === 'expired' && 'This offer has expired'}
                {offer.availability === 'sold_out' && 'This offer is sold out'}
                {offer.availability === 'unavailable' && 'This offer is no longer available'}
              </Heading>
              <Text size="2" color="gray">
                If you bought it before, you can still open it below.
              </Text>
            </Card>
          </Rise>
        )}

        {!unlocked && (
          <Rise delay={240}>
            <div style={{ textAlign: 'center', margin: 'var(--space-5) 0' }}>
              {restoreError && (
                <Text size="2" color="red" style={{ display: 'block', marginBottom: 'var(--space-2)' }}>
                  {restoreError}
                </Text>
              )}
              {restoring ? (
                <Spinner size="1" />
              ) : (
                <Button variant="ghost" asChild>
                  <a href={restoreHref}>Already purchased? Restore purchase</a>
                </Button>
              )}
            </div>
          </Rise>
        )}

        <Text
          size="1"
          color="gray"
          style={{ display: 'block', textAlign: 'center', marginTop: 'var(--space-6)', paddingBottom: 'var(--space-4)' }}
        >
          Payments secured by Whop
        </Text>
      </div>
    </div>
  );
}

function CenteredShell({ children }: { children: React.ReactNode }) {
  return (
    <div
      style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 'var(--space-4)',
        backgroundColor: 'var(--color-background)',
      }}
    >
      {children}
    </div>
  );
}
